import React from "react";
import { motion } from "framer-motion";
import {
  BrainCircuit,
  Bot,
  BarChart3,
  Database,
  LineChart,
  Code2,
  Cloud,
  TrendingUp,
  Briefcase,
  FileBarChart,
  Compass,
  Palette,
  Pen,
  LayoutDashboard,
  Share2,
} from "lucide-react";

const CATEGORIES = [
  {
    id: "tech",
    label: "Tech & Intelligence",
    description:
      "From models to production. We design AI systems, full-stack platforms and cloud infrastructure that scale with intent.",
    span: "md:col-span-7",
    image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9ufGVufDB8fHx8MTc4MTQyOTYwMXww&ixlib=rb-4.1.0&q=85",
    services: [
      { icon: BrainCircuit, label: "Machine Learning Solutions" },
      { icon: Bot, label: "Artificial Intelligence Solutions" },
      { icon: BarChart3, label: "Data Analytics" },
      { icon: Database, label: "SQL Development" },
      { icon: LineChart, label: "Data Visualization" },
      { icon: Code2, label: "Full-Stack Development" },
      { icon: Cloud, label: "Cloud Solutions & Infrastructure" },
    ],
  },
  {
    id: "finance",
    label: "Finance & Business Intelligence",
    description:
      "Numbers turned into narrative. Strategy, modeling and reporting that move boards — and bottom lines.",
    span: "md:col-span-5",
    image: "https://images.pexels.com/photos/17483870/pexels-photo-17483870.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    services: [
      { icon: TrendingUp, label: "Financial Analysis" },
      { icon: Briefcase, label: "Business Analysis" },
      { icon: FileBarChart, label: "Performance Reporting" },
      { icon: Compass, label: "Forecasting & Strategic Planning" },
    ],
  },
  {
    id: "design",
    label: "Graphics & Design",
    description:
      "Brand systems with a beating heart. Identity, product and social design that earn attention and trust.",
    span: "md:col-span-12",
    services: [
      { icon: Palette, label: "Brand Identity Design" },
      { icon: Pen, label: "Logo Design" },
      { icon: LayoutDashboard, label: "UI/UX Design" },
      { icon: Share2, label: "Social Media Design" },
    ],
  },
];

const SectionHeader = () => (
  <div className="max-w-3xl">
    <div
      className="text-xs font-mono uppercase tracking-[0.3em] text-blue-400"
      data-testid="services-overline"
    >
      [ Services / 03 ]
    </div>
    <h2
      className="mt-5 font-display text-4xl md:text-5xl font-medium tracking-tight"
      data-testid="services-heading"
    >
      A multidisciplinary studio
      <br />
      <span className="text-zinc-500">built for what comes next.</span>
    </h2>
    <p className="mt-5 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
      Three practices, one team. We blend engineering, financial rigor and visual craft so your product ships smarter — and looks the part.
    </p>
  </div>
);

const CategoryCard = ({ cat, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`relative ${cat.span} col-span-1 group`}
    data-testid={`service-category-${cat.id}`}
  >
    <div className="relative h-full glass card-hover rounded-2xl p-8 md:p-10 overflow-hidden">
      {cat.image && (
        <>
          <div
            className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity"
            style={{
              backgroundImage: `url(${cat.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#050505]/85 via-[#050505]/70 to-[#050505]/95" />
        </>
      )}

      <div className="relative">
        <div className="flex items-baseline justify-between">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">
            0{index + 1} · Practice
          </div>
          <div className="text-xs font-mono text-blue-400">{cat.services.length} services</div>
        </div>

        <h3
          className="mt-4 font-display text-2xl md:text-3xl font-medium tracking-tight"
          data-testid={`service-category-title-${cat.id}`}
        >
          {cat.label}
        </h3>
        <p className="mt-3 text-zinc-400 text-sm md:text-base leading-relaxed max-w-xl">
          {cat.description}
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {cat.services.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`service-item-${cat.id}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-blue-500/30 transition-colors"
              >
                <Icon className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm text-zinc-200">{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </motion.div>
);

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 md:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeader />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {CATEGORIES.map((cat, i) => (
            <CategoryCard key={cat.id} cat={cat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
